import { FC, useEffect, useState } from 'react';
import { Box, Link, Spinner, Text, Icon, Center } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { protocol } from 'shared/constants';
import { ReactComponent as BrandLogo } from 'resources/images/stacked-default.svg';
import { ReactComponent as CheckmarkIcon } from 'resources/images/checkmark.svg';
import { CenteredContainer } from 'app/components/Core';
import { BrandPaper } from 'app/components/BrandPaper';
import { UnsupportedMobile } from 'app/components/UnsupportedMobile';

const LOADING_DURATION_SECONDS_DEFAULT = 4;

interface AppRedirectProps {
  loadingDurationSeconds?: number;
}

export const AppRedirect: FC<AppRedirectProps> = ({
  loadingDurationSeconds = LOADING_DURATION_SECONDS_DEFAULT,
}) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const { path, ...search } = queryString.parse(location.search);
  const originalPath = path?.toString().substring(1) || '/';
  const originalSearch = queryString.stringify(search);
  const fullUrl = `${originalPath}?${originalSearch}`;
  const deeplink = `${protocol}://${fullUrl}`;

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, loadingDurationSeconds * 1000);

    window.location.replace(deeplink);
  }, [deeplink, fullUrl, loadingDurationSeconds]);

  return (
    <UnsupportedMobile>
      <CenteredContainer width={640} p={4}>
        <Box mb={8}>
          <Icon
            as={BrandLogo}
            data-testid="brand-logo"
            height={139}
            width="auto"
            fill="M.70"
            outline="none"
          />
        </Box>
        <BrandPaper minHeight={367} p={2} data-testid="app-redirect">
          {loading ? (
            <Spinner size="xl" thickness="4px" label="" />
          ) : (
            <Center
              color="B.80"
              borderRadius="50%"
              border="3.5px solid"
              width="80px"
              height="80px"
            >
              <Icon
                as={CheckmarkIcon}
                width="36px"
                height="36px"
                mt="4px"
                ml="2px"
              />
            </Center>
          )}

          <Text
            fontWeight={600}
            fontSize="xl"
            lineHeight="29px"
            maxWidth={340}
            pt={8}
            pb={1}
          >
            Launching Session Studio
          </Text>
          <Box height="44px">
            {loading ? (
              <Text fontSize="md" color="textSubdued">
                Loading...
              </Text>
            ) : (
              <>
                <Text fontSize="md" color="textSubdued">
                  Click&nbsp;
                  <q>
                    <b>Open Session Studio</b>
                  </q>
                  &nbsp;to launch the desktop app.
                </Text>
                <Text fontSize="md" color="textSubdued">
                  Not working? You can also&nbsp;
                  <Link
                    to="/"
                    as={RouterLink}
                    textDecoration="none"
                    color="B.80"
                  >
                    use Session Studio in your browser
                  </Link>
                  .
                </Text>
              </>
            )}
          </Box>
        </BrandPaper>
      </CenteredContainer>
    </UnsupportedMobile>
  );
};
